import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import './VersionModal.scss';

/**
 * VersionModal
 * Renders the "Thông tin phiên bản" popup using the new full-page layout (ver_vi.html design).
 *
 * Props:
 *   isOpen  {bool}     - whether the modal is visible
 *   onClose {func}     - close handler
 *   version {object}   - share.version from Redux (id, updatedAt, content, contentEn, contentKh)
 *   lang    {string}   - current language code ("vi" | "en" | "kh")
 */
export default function VersionModal({ isOpen, onClose, version, lang }) {
  const { t } = useTranslation();
  const overlayRef = useRef(null);

  // Close on overlay click
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const vObj = version || {};
  const versionId = vObj.id || '4.5.1';
  const updatedAt = vObj.updatedAt
    ? moment(vObj.updatedAt).format('DD/MM/YYYY')
    : '09/05/2026';

  // Pick content based on language
  let content = vObj.content || '';
  if (lang === 'en' && vObj.contentEn) content = vObj.contentEn;
  if (lang === 'kh' && vObj.contentKh) content = vObj.contentKh;

  // Fallback mock data if API returns empty content (for UI testing)
//   if (!content) {
//     content = `
// <!-- Section label -->
// <div class="section-label">
//   <svg viewBox="0 0 24 24" fill="none"><path d="M4 5h16M4 12h16M4 19h16"/></svg>
//   Extension
// </div>

// <div class="feature-list">
  
//   <!-- Feature card nổi bật (highlight) -->
//   <div class="feature-card highlight">
//     <div class="feature-icon">
//       <svg viewBox="0 0 24 24" fill="none" stroke="#16A34A"><path d="M13 2L4 14h7l-1 8 10-13h-7z"/></svg>
//     </div>
//     <div class="feature-col">
//       <div class="feature-hd">
//         <span class="feature-title">Gửi tin nhắn tự động khi chia tệp</span>
//         <span class="badge-new">Nổi bật</span>
//       </div>
//       <div class="feature-desc">Bật tự động là hệ thống tự gửi lần lượt từng tệp đến khi xong — không cần thao tác lặp lại, phù hợp chiến dịch quy mô lớn.</div>
//       <div class="feature-more">
//         <button class="more-toggle">
//           Xem chi tiết
//           <svg viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5"/></svg>
//         </button>
//         <ul class="more-detail">
//           <li>Cho phép lựa chọn linh hoạt giữa gửi tự động hoặc gửi thủ công khi chia tệp khách hàng.</li>
//           <li>Khi bật chế độ tự động, hệ thống sẽ tự động gửi lần lượt từng tệp cho đến khi hoàn tất toàn bộ danh sách.</li>
//           <li>Không cần thao tác lặp lại, giúp tiết kiệm thời gian và giảm sai sót trong quá trình gửi.</li>
//           <li>Phù hợp với các chiến dịch quy mô lớn, đảm bảo quá trình gửi tin diễn ra liên tục và hiệu quả.</li>
//         </ul>
//       </div>
//     </div>
//   </div>

//   <!-- Feature card thường -->
//   <div class="feature-card">
//     <div class="feature-icon">
//       <svg viewBox="0 0 24 24" fill="none" stroke="#6B7280"><path d="M12 3v3m0 12v3m9-9h-3M6 12H3m15.4-6.4l-2.1 2.1M8.7 15.7l-2.1 2.1m12.8 0l-2.1-2.1M8.7 8.3L6.6 6.2"/><circle cx="12" cy="12" r="3"/></svg>
//     </div>
//     <div class="feature-col">
//       <div class="feature-hd">
//         <span class="feature-title">Tự động định dạng tin nhắn (Auto-format)</span>
//       </div>
//       <div class="feature-desc">Nút "BomBot định dạng" tự tạo spin nội dung và cá nhân hoá tên/giới tính chỉ với 1 thao tác, giảm nguy cơ bị đánh dấu spam.</div>
//       <div class="feature-more">
//         <button class="more-toggle">
//           Xem chi tiết
//           <svg viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5"/></svg>
//         </button>
//         <ul class="more-detail">
//           <li>Hỗ trợ nút "BomBot định dạng" giúp tự động xử lý nội dung chỉ với 1 thao tác.</li>
//           <li>Tự động phát hiện và tạo spin nội dung (biến thể câu chữ), tránh gửi hàng loạt một nội dung giống nhau.</li>
//           <li>Nhận diện các từ/cụm từ có thể cá nhân hoá (tên, giới tính,...) và thay thế tự động.</li>
//           <li>Giúp tin nhắn đa dạng, tự nhiên hơn, giảm nguy cơ spam và tăng hiệu quả tiếp cận khách hàng.</li>
//         </ul>
//       </div>
//     </div>
//   </div>

// </div>
//     `;
//   }

  // Handle expand/collapse clicks inside the raw HTML
  const handleContentClick = (e) => {
    const toggleBtn = e.target.closest('.more-toggle');
    if (toggleBtn) {
      const detailUl = toggleBtn.nextElementSibling;
      if (detailUl && detailUl.classList.contains('more-detail')) {
        const isOpen = detailUl.classList.toggle('open');
        toggleBtn.classList.toggle('open', isOpen);
        // The text is typically the first child node before the SVG
        if (toggleBtn.childNodes.length > 0) {
          toggleBtn.childNodes[0].textContent = isOpen ? 'Thu gọn ' : 'Xem chi tiết ';
        }
      }
    }
  };

  return (
    <div
      className="vm-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      <div className="vm-page">
        {/* ── HEADER ── */}
        <div className="vm-hd">
          <div className="vm-hd-row">
            <div>
              <div className="vm-eyebrow">
                <svg viewBox="0 0 20 20">
                  <path d="M10 0l2.4 6.2L19 8l-6.6 1.8L10 16l-2.4-6.2L1 8l6.6-1.8z" />
                </svg>
                {t('version.title', 'Thông tin phiên bản')}
              </div>
              <div className="vm-version-row">
                <span className="vm-version">Version {versionId}</span>
                {updatedAt && (
                  <span className="vm-date-pill">Cập nhật {updatedAt}</span>
                )}
              </div>
            </div>
            <button className="vm-close" onClick={onClose} title="Đóng">
              <svg viewBox="0 0 14 14" fill="none">
                <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── BODY ── */}
        <div className="vm-body">
          {content ? (
            <div
              className="vm-content"
              dangerouslySetInnerHTML={{ __html: content }}
              onClick={handleContentClick}
            />
          ) : (
            <p className="vm-empty">{t('setting_account.version_no_detail', 'Chưa có thông tin chi tiết.')}</p>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="vm-ft">
          <span className="vm-ft-meta">
            {versionId ? `Version ${versionId}` : ''}
          </span>
          <button className="vm-btn-primary" onClick={onClose}>
            {t('setting_account.version_understood', 'Đã hiểu')}
          </button>
        </div>
      </div>
    </div>
  );
}
